/**
 * Composant PWA Manager pour Squash Scoring App
 * 
 * Gère l'interface utilisateur pour les fonctionnalités PWA:
 * - Bouton d'installation
 * - Notifications de mise à jour
 * - Indicateur hors-ligne
 * - Gestion des erreurs
 * 
 * @module components/PWAManager
 * @version 1.0.0
 */

import React, { useState, useEffect } from 'react';
import { usePWA } from '../hooks/usePWA';
import { useTranslation } from '../i18n/hooks/useTranslation';

// Interface pour les props du composant
interface PWAManagerProps {
  className?: string;
  showInstallButton?: boolean;
  showUpdateNotification?: boolean;
  showOfflineIndicator?: boolean;
  children?: React.ReactNode;
}

/**
 * Composant principal pour gérer l'interface PWA
 */
export const PWAManager: React.FC<PWAManagerProps> = ({
  className = '',
  showInstallButton = true,
  showUpdateNotification = true,
  showOfflineIndicator = true,
  children
}) => {
  const {
    isInstallable,
    isInstalled,
    isServiceWorkerUpdated,
    isOnline,
    promptInstall,
    reloadApp,
    clearCache,

    // ✅ NOUVEAUX imports pour iOS
    isIOS,
    showIOSInstallPrompt,
    hideIOSInstructions
  } = usePWA();

  const { t } = useTranslation();

  // États locaux pour l'UI
  const [showUpdateBanner, setShowUpdateBanner] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const [installError, setInstallError] = useState<string | null>(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);

  // Affiche la bannière de mise à jour quand nécessaire
  useEffect(() => {
    if (isServiceWorkerUpdated && showUpdateNotification) {
      setShowUpdateBanner(true);
    }
  }, [isServiceWorkerUpdated, showUpdateNotification]);

  // Affiche le prompt d'installation après un délai
  useEffect(() => {
    if (isInstallable && !isInstalled && showInstallButton) {
      const timer = setTimeout(() => {
        setShowInstallPrompt(true);
      }, 3000); // Attendre 3 secondes avant d'afficher

      return () => clearTimeout(timer);
    }
  }, [isInstallable, isInstalled, showInstallButton]);

  /**
   * Gère l'installation de l'app
   */
  const handleInstall = async () => {
    try {
      setIsInstalling(true);
      setInstallError(null);
      await promptInstall();
      setShowInstallPrompt(false);
    } catch (error) {
      console.error('[PWA] Erreur installation:', error);
      setInstallError(t('components.pwa.install.error'));
    } finally {
      setIsInstalling(false);
    }
  };

  /**
   * Gère la mise à jour de l'app
   */
  const handleUpdate = () => {
    reloadApp();
  };

  // clearCache est disponible via usePWA() si besoin
  void clearCache; // Marquer comme utilisé intentionnellement

  /**
   * Ferme la bannière de mise à jour
   */
  const dismissUpdateBanner = () => {
    setShowUpdateBanner(false);
  };

  /**
   * Ferme le prompt d'installation
   */
  const dismissInstallPrompt = () => {
    setShowInstallPrompt(false);
  };

  return (
    <div className={`pwa-manager ${className}`}>
      {/* Bannière de mise à jour */}
      {showUpdateBanner && (
        <UpdateBanner
          onUpdate={handleUpdate}
          onDismiss={dismissUpdateBanner}
        />
      )}

      {/* Indicateur hors-ligne */}
      {showOfflineIndicator && !isOnline && (
        <OfflineIndicator />
      )}

      {/* Bouton d'installation - Android/Chrome */}
      {showInstallButton && showInstallPrompt && isInstallable && !isInstalled && !isIOS && (
        <InstallButton
          onClick={handleInstall}
          onDismiss={dismissInstallPrompt}
          isLoading={isInstalling}
          error={installError}
        />
      )}

      {/* ✅ NOUVEAU : Bouton d'installation - iOS Safari */}
      {showInstallButton && showIOSInstallPrompt && !isInstalled && isIOS && (
        <IOSInstallButton
          onDismiss={hideIOSInstructions}
        />
      )}

      {/* Contenu enfant */}
      {children}
    </div>
  );
};

/**
 * Composant pour la bannière de mise à jour
 */
interface UpdateBannerProps {
  onUpdate: () => void;
  onDismiss: () => void;
}

const UpdateBanner: React.FC<UpdateBannerProps> = ({ onUpdate, onDismiss }) => {
  const { t } = useTranslation();

  return (
    <div className="fixed top-0 left-0 right-0 bg-blue-600 text-white p-4 z-50">
      <div className="flex items-center justify-between max-w-md mx-auto">
        <div className="flex-1">
          <p className="text-sm font-medium">
            ✨ {t('components.pwa.update.availableTitle')}
          </p>
          <p className="text-xs opacity-90">
            {t('components.pwa.update.availableSubtitle')}
          </p>
        </div>
        <div className="flex gap-2 ml-4">
          <button
            onClick={onUpdate}
            className="bg-white text-blue-600 px-3 py-1 text-sm font-medium hover:bg-gray-100 transition-colors"
          >
            {t('components.pwa.update.action')}
          </button>
          <button
            onClick={onDismiss}
            className="text-white hover:text-gray-200 transition-colors"
            aria-label={t('common.close')}
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Composant pour l'indicateur hors-ligne
 */
const OfflineIndicator: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div
      className="fixed top-2 left-1/2 -translate-x-1/2 bg-orange-500 text-white px-4 py-2 z-40 pointer-events-none"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center gap-2">
        <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
        <span className="text-sm font-medium">{t('components.pwa.offlineMode')}</span>
      </div>
    </div>
  );
};

/**
 * Composant pour le bouton d'installation
 */
interface InstallButtonProps {
  onClick: () => void;
  onDismiss: () => void;
  isLoading: boolean;
  error: string | null;
}

const InstallButton: React.FC<InstallButtonProps> = ({ 
  onClick, 
  onDismiss, 
  isLoading, 
  error 
}) => {
  const { t } = useTranslation();

  return (
  <div className="fixed bottom-4 right-4 z-40">
    <div className="bg-white border border-gray-200 p-4 max-w-sm">
      {error && (
        <div className="mb-3 p-2 bg-red-50 border border-red-200 text-red-700 text-xs">
          {error}
        </div>
      )}
      
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 bg-green-600 flex items-center justify-center flex-shrink-0">
          <span className="text-white text-xl">🏆</span>
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 text-sm mb-1">
            {t('components.pwa.install.title')}
          </h3>
          <p className="text-gray-600 text-xs mb-3">
            {t('components.pwa.install.subtitle')}
          </p>
          
          <button
            onClick={onClick}
            disabled={isLoading}
            className="w-full bg-green-600 text-white px-4 py-2 text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? t('components.pwa.install.installing') : t('components.pwa.install.action')}
          </button>
        </div>
        
        <button
          onClick={onDismiss}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          aria-label={t('common.close')}
        >
          ✕
        </button>
      </div>
    </div>
  </div>
  );
};

/**
 * Composant spécifique pour l'installation sur iOS
 */
interface IOSInstallButtonProps {
  onDismiss: () => void;
}

const IOSInstallButton: React.FC<IOSInstallButtonProps> = ({ onDismiss }) => {
  const { t } = useTranslation();

  return (
  <div className="fixed bottom-4 right-4 z-40">
    <div className="bg-white border border-gray-200 p-4 max-w-sm">
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 bg-blue-600 flex items-center justify-center flex-shrink-0">
          <span className="text-white text-xl">📱</span>
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 text-sm mb-2">
            {t('components.pwa.installIOS.title')}
          </h3>
          <div className="text-gray-600 text-xs space-y-2">
            <div className="flex items-center gap-2">
              <span>1.</span>
              <span>{t('components.pwa.installIOS.step1')}</span>
              <div className="inline-flex items-center px-1 py-0.5 bg-gray-100 rounded text-xs">
                📤
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span>2.</span>
              <span>{t('components.pwa.installIOS.step2')}</span>
            </div>
            <div className="flex items-center gap-2">
              <span>3.</span>
              <span>{t('components.pwa.installIOS.step3')}</span>
            </div>
          </div>
        </div>
        
        <button
          onClick={onDismiss}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          aria-label={t('common.close')}
        >
          ✕
        </button>
      </div>
    </div>
  </div>
  );
};

/**
 * Composant de notification toast personnalisable
 */
interface ToastProps {
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  isVisible: boolean;
  onClose: () => void;
  duration?: number;
}

const Toast: React.FC<ToastProps> = ({ 
  message, 
  type, 
  isVisible, 
  onClose, 
  duration = 3000 
}) => {
  useEffect(() => {
    if (isVisible && duration > 0) {
      const timer = setTimeout(onClose, duration);
      return () => clearTimeout(timer);
    }
  }, [isVisible, duration, onClose]);

  if (!isVisible) return null;

  const bgColors = {
    success: 'bg-green-500',
    error: 'bg-red-500',
    warning: 'bg-orange-500',
    info: 'bg-blue-500'
  };

  const icons = {
    success: '✅',
    error: '❌',
    warning: '⚠️',
    info: 'ℹ️'
  };

  return (
    <div className={`fixed bottom-20 left-4 right-4 ${bgColors[type]} text-white px-4 py-3 z-40 max-w-sm mx-auto`}>
      <div className="flex items-center gap-2">
        <span>{icons[type]}</span>
        <span className="text-sm">{message}</span>
        <button onClick={onClose} className="ml-auto text-white hover:text-gray-200">
          ✕
        </button>
      </div>
    </div>
  );
};

/**
 * Hook pour gérer les toasts PWA
 */
export const usePWAToast = () => {
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
    isVisible: boolean;
  }>({
    message: '',
    type: 'info',
    isVisible: false
  });

  const showToast = (message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    setToast({ message, type, isVisible: true });
  };

  const hideToast = () => {
    setToast(prev => ({ ...prev, isVisible: false }));
  };

  const ToastComponent = () => (
    <Toast
      message={toast.message}
      type={toast.type}
      isVisible={toast.isVisible}
      onClose={hideToast}
    />
  );

  return { showToast, hideToast, ToastComponent };
};

/**
 * Hook pour persister les préférences PWA
 */
export const usePWAPreferences = () => {
  const [preferences, setPreferences] = useState({
    hideInstallPrompt: false,
    autoUpdate: true,
    showOfflineToast: true,
    installPromptDismissed: false
  });

  useEffect(() => {
    // Charger les préférences depuis localStorage
    try {
      const saved = localStorage.getItem('pwa-preferences');
      if (saved) {
        setPreferences(prev => ({ ...prev, ...JSON.parse(saved) }));
      }
    } catch (error) {
      console.warn('[PWA] Erreur chargement préférences:', error);
    }
  }, []);

  const updatePreference = (key: keyof typeof preferences, value: boolean) => {
    const newPreferences = { ...preferences, [key]: value };
    setPreferences(newPreferences);
    
    try {
      localStorage.setItem('pwa-preferences', JSON.stringify(newPreferences));
    } catch (error) {
      console.warn('[PWA] Erreur sauvegarde préférences:', error);
    }
  };

  return { preferences, updatePreference };
};

/**
 * Hook pour les statistiques PWA (optionnel)
 */
export const usePWAStats = () => {
  const [stats, setStats] = useState({
    installDate: null as string | null,
    sessionCount: 0,
    offlineUsage: 0,
    cacheHits: 0
  });

  useEffect(() => {
    try {
      const saved = localStorage.getItem('pwa-stats');
      if (saved) {
        setStats(JSON.parse(saved));
      }
    } catch (error) {
      console.warn('[PWA] Erreur chargement stats:', error);
    }
  }, []);

  const updateStats = (updates: Partial<typeof stats>) => {
    const newStats = { ...stats, ...updates };
    setStats(newStats);
    
    try {
      localStorage.setItem('pwa-stats', JSON.stringify(newStats));
    } catch (error) {
      console.warn('[PWA] Erreur sauvegarde stats:', error);
    }
  };

  return { stats, updateStats };
};

/**
 * Composant wrapper simplifié pour une intégration rapide
 */
export const PWAWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <PWAManager
      showInstallButton={true}
      showUpdateNotification={true}
      showOfflineIndicator={true}
    >
      {children}
    </PWAManager>
  );
};

/**
 * Composant PWA avec configuration avancée
 */
interface PWAManagerAdvancedProps extends PWAManagerProps {
  enableToasts?: boolean;
  enableStats?: boolean;
  customInstallPrompt?: React.ComponentType<{
    onInstall: () => void;
    onDismiss: () => void;
    isLoading: boolean;
  }>;
}

export const PWAManagerAdvanced: React.FC<PWAManagerAdvancedProps> = ({
  enableToasts = false,
  enableStats = false,
  customInstallPrompt: CustomInstallPrompt,
  ...props
}) => {
  const { showToast, ToastComponent } = usePWAToast();
  const { updateStats } = usePWAStats();
  const { isInstalled, isOnline } = usePWA();
  const { t } = useTranslation();

  // Notifications automatiques avec toasts
  useEffect(() => {
    if (enableToasts) {
      if (isInstalled) {
        showToast(t('components.pwa.install.success'), 'success');
      }
      if (!isOnline) {
        showToast(t('components.pwa.offlineMode'), 'warning');
      }
    }
  }, [isInstalled, isOnline, enableToasts, showToast, t]);

  // Statistiques automatiques
  useEffect(() => {
    if (enableStats) {
      updateStats({ sessionCount: Date.now() });
    }
  }, [enableStats, updateStats]);

  return (
    <>
      <PWAManager {...props} />
      {enableToasts && <ToastComponent />}
    </>
  );
};

export default PWAManager;